import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import PasswordInput from "@/components/PasswordInput";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("La password deve avere almeno 8 caratteri"); return; }
    if (password !== confirm) { setError("Le password non coincidono"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password aggiornata");
      setTimeout(() => navigate("/auth"), 2200);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore durante la reimpostazione";
      setError(typeof msg === "string" ? msg : "Errore durante la reimpostazione");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-rose-warm mx-auto mb-4" />
          <h2 className="font-display text-2xl font-medium mb-2">Link non valido</h2>
          <p className="text-secondary-fg mb-6">Manca il token di reset. Il link nell&apos;email potrebbe essere danneggiato.</p>
          <Link to="/forgot-password" data-testid="reset-request-new" className="text-brand hover:underline">Richiedi un nuovo link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app grid md:grid-cols-2">
      <div className="hidden md:flex items-end p-12 relative bg-gradient-to-br from-brand/20 to-sage/20">
        <div className="relative z-10">
          <BrandLogo size={48} />
          <h1 className="font-display text-4xl font-medium text-primary-fg mt-6">Family Brain</h1>
          <p className="text-secondary-fg mt-2 max-w-md">Scegli una password nuova e sicura per il tuo account.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          {!done ? (
            <>
              <h2 className="font-display text-3xl font-medium mb-2">Reimposta password</h2>
              <p className="text-secondary-fg mb-8">Scegli una nuova password per il tuo account Family Brain.</p>
              <form onSubmit={submit} data-testid="reset-password-form" className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Nuova password</label>
                  <PasswordInput
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Almeno 8 caratteri"
                    disabled={loading}
                    className="input-warm w-full pl-10 pr-10"
                    data-testid="reset-password-new"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Conferma password</label>
                  <PasswordInput
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Ripeti la nuova password"
                    disabled={loading}
                    className="input-warm w-full pl-10 pr-10"
                    data-testid="reset-password-confirm"
                  />
                </div>
                {error && (
                  <p data-testid="reset-password-error" className="text-sm text-rose-warm bg-rose-warm/10 border border-rose-warm/30 rounded-xl p-3">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={loading || !password || !confirm}
                  data-testid="reset-password-submit"
                  className="w-full bg-brand text-white rounded-full py-3 font-medium hover:opacity-90 disabled:opacity-50"
                >
                  {loading ? "Salvo..." : "Aggiorna password"}
                </button>
              </form>
              <p className="text-xs text-tertiary-fg mt-6 text-center">
                Il link è monouso: dopo aver salvato non potrai riutilizzarlo.
              </p>
            </>
          ) : (
            <div data-testid="reset-password-done" className="text-center">
              <div className="w-16 h-16 rounded-full bg-sage/20 mx-auto flex items-center justify-center mb-6">
                <CheckCircle2 className="w-8 h-8 text-sage" />
              </div>
              <h2 className="font-display text-3xl font-medium mb-3">Password aggiornata</h2>
              <p className="text-secondary-fg mb-6">Ti stiamo reindirizzando al login...</p>
              <Link to="/auth" className="text-brand hover:underline text-sm">Vai al login ora</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
